import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Database, Eye, EyeOff, FolderPlus, Palette, Share2, Trash2, Upload, Settings2 } from 'lucide-react';
import LayerStyleEditor from './LayerStyleEditor';
import { legendItems } from '@/utils/layerStyle';
import workspaceService, { CompanyLayer, Workspace, hasWorkspaces } from '@/services/workspace.service';
import { useMapStore } from '@/store/map.store';
import { forget, hide, isShown, show } from '@/utils/companyLayers';
import { useToast } from '@/components/common/Toast';
import { cn } from '@/utils/cn';
import { WorkspaceReports } from '@/components/ReportStudio/WorkspaceReports';
import UploadWizard from '@/components/upload/UploadWizard';
import { Button } from '@/components/common/Button';
import type { Upload as UploadRecord } from '@/types/upload';
import UploadStatusBadge from '@/components/upload/UploadStatusBadge';
import { useUploads } from '@/hooks/useUploads';
import { useUploadRights } from '@/hooks/useUploadRights';
import { isImportInProgress } from '@/utils/upload';
import { AddToGroupMenu } from './groups/AddToGroupMenu';
import { useMapLayerChanges, useOnlyInMyMenus } from './groups/useLayerGroups';
import { PenLine } from 'lucide-react';
import { editable, useEditingStore } from '@/components/editing/editing.store';

/**
 * The company's own layers, workspace by workspace (Veramenta docs/en/59 sec.3.2): make and share a workspace, put a
 * file into it as a layer, show it on the map, say what its columns are called and which are money or personal,
 * delete it. Who may do what in a workspace is the role they hold there.
 */

const input = 'w-full px-2 py-1 border border-gn-light rounded bg-gn-surface text-gn-primary text-xs';
const small = 'p-1 rounded hover:bg-gn-tertiary text-gn-secondary disabled:opacity-40';
const CLASSES = ['none', 'money', 'contact', 'pii', 'premium'];

/** The company layers already switched on once, at the map's opening, in this page's life. */
const startedLayers = new Set<string>();

export const WorkspacePanel: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').substring(0, 2);
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const map = useMapStore((s) => s.mapInstance);
  const [open, setOpen] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [ums, setUms] = useState(false);
  const [, redraw] = useState(0);

  useEffect(() => { hasWorkspaces().then(setUms); }, []);

  const { data: workspaces = [] } = useQuery({ queryKey: ['workspaces'], queryFn: () => workspaceService.list() });

  // The layers said to be switched on when the map opens are drawn once, when it has opened; after that the
  // eye is the person's.
  const { data: everyLayer = [] } = useQuery({
    queryKey: ['workspaceLayers', 'all'],
    queryFn: () => workspaceService.allLayers(),
    retry: false,
  });
  useEffect(() => {
    if (!map) return;
    const listed = new Set(workspaces.map((w) => w.id));
    let any = false;
    for (const layer of everyLayer) {
      if (layer.visible && !layer.dataOnly && listed.has(layer.workspace) && !startedLayers.has(layer.name)) {
        startedLayers.add(layer.name);
        show(map, layer);
        any = true;
      }
    }
    if (any) redraw((n) => n + 1);
  }, [map, everyLayer, workspaces]);

  const create = useMutation({
    mutationFn: () => workspaceService.create(newName.trim()),
    onSuccess: (id) => {
      setNewName('');
      setExpanded(id);
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
    onError: (e: Error) => addToast(e.message, 'error'),
  });

  return (
    <div className="border-b border-gn-light">
      <button type="button" onClick={() => setOpen(!open)}
              className="flex items-center justify-between w-full px-3 py-2 rounded hover:bg-gn-tertiary text-xs font-medium text-gn-primary">
        <span>{t('workspaces.title', { defaultValue: 'My workspaces' })}</span>
        <ChevronDown size={14} className={cn('transition-transform text-gn-secondary', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="px-2 pb-2 space-y-1">
          {workspaces.map((w) => (
            <WorkspaceItem key={w.id} workspace={w} expanded={expanded === w.id} ums={ums}
                           onToggle={() => setExpanded(expanded === w.id ? null : w.id)} lang={lang}
                           onChanged={() => redraw((n) => n + 1)} mapReady={!!map} />
          ))}
          {ums && (
            <div className="flex gap-1 pt-1">
              <input className={input} placeholder={t('workspaces.new', { defaultValue: 'New workspace' })} value={newName}
                     onChange={(e) => setNewName(e.target.value)} />
              <button type="button" className={small} disabled={!newName.trim() || create.isPending} onClick={() => create.mutate()}
                      title={t('workspaces.create', { defaultValue: 'Make' })}>
                <FolderPlus size={14} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const WorkspaceItem: React.FC<{
  workspace: Workspace;
  expanded: boolean;
  ums: boolean;
  lang: string;
  mapReady: boolean;
  onToggle: () => void;
  onChanged: () => void;
}> = ({ workspace, expanded, ums, lang, onToggle, onChanged }) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const map = useMapStore((s) => s.mapInstance);
  // A layer switched from one of my menus redraws its eye here; one I keep only in my menus is not listed here.
  useMapLayerChanges(map);
  const onlyInMyMenus = useOnlyInMyMenus();
  const [uploading, setUploading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [styling, setStyling] = useState<CompanyLayer | null>(null);
  const editingKey = useEditingStore((s) => s.layer?.name ?? null);
  const startEditing = useEditingStore((s) => s.start);
  const stopEditing = useEditingStore((s) => s.stop);

  const { data: listed = [] } = useQuery({
    queryKey: ['workspaceLayers', workspace.id],
    queryFn: () => workspaceService.layers(workspace.id),
    enabled: expanded,
  });
  // The workspace's map layers, and the company's data without a place (no map, so no eye).
  const layers = listed.filter((l) => !l.dataOnly && !onlyInMyMenus.company.has(l.key));
  const dataItems = listed.filter((l) => l.dataOnly);
  const { data: rights } = useUploadRights(workspace.id, expanded);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['workspaceLayers', workspace.id] });

  useEffect(() => {
    if (!map || toShow.current.size === 0) return;
    for (const l of layers) {
      if (toShow.current.has(l.key)) {
        toShow.current.delete(l.key);
        show(map, l);
        onChanged();
      }
    }
  }, [listed, map]); // eslint-disable-line react-hooks/exhaustive-deps

  // Uploads still waiting for their check or importing.
  const { data: uploads = [] } = useUploads(workspace.id, expanded);
  const running = uploads.filter((u) => u.status === 'WAITING' || isImportInProgress(u.status));

  // An import that ends while this workspace is open: its layer is listed. An imported upload leaves the list (the
  // server keeps no trace of it), so one that was importing and is gone, or failed, has ended.
  const wasRunning = useRef<Set<string>>(new Set());
  const toShow = useRef<Set<string>>(new Set());
  useEffect(() => {
    const now = new Map(uploads.map((u) => [u.id, u]));
    let finished = false;
    for (const id of Array.from(wasRunning.current)) {
      const u = now.get(id);
      if (!u || !isImportInProgress(u.status)) {
        wasRunning.current.delete(id);
        finished = true;
      }
    }
    for (const u of uploads) if (isImportInProgress(u.status)) wasRunning.current.add(u.id);
    if (finished) layersChanged();
  }, [uploads]); // eslint-disable-line react-hooks/exhaustive-deps

  const layersChanged = () => {
    refresh();
    queryClient.invalidateQueries({ queryKey: ['workspaceLayers', 'all'] });
  };

  /** The dialog says an import is in the database; the layer is listed, and shown when the person asked to. */
  const imported = (u: UploadRecord, showOnMap: boolean) => {
    if (showOnMap && u.layerKey) toShow.current.add(u.layerKey);
    layersChanged();
  };

  const remove = useMutation({
    mutationFn: (layer: CompanyLayer) => workspaceService.remove(workspace.id, layer.key).then(() => layer),
    onSuccess: (layer) => {
      if (map && !layer.dataOnly) forget(map, layer);
      refresh();
    },
    onError: (e: any) => addToast(e?.response?.data?.error ?? e.message, 'error'),
  });

  const toggle = (layer: CompanyLayer) => {
    if (!map) return;
    if (isShown(map, layer)) hide(map, layer);
    else show(map, layer);
    onChanged();
  };

  return (
    <div className="border border-gn-light rounded">
      <div className="flex items-center justify-between px-2 py-1">
        <button type="button" onClick={onToggle} className="flex-1 text-left text-xs font-medium truncate">{workspace.name}</button>
        {ums && workspace.canManage && (
          <button type="button" className={small} onClick={() => setSharing(!sharing)} title={t('workspaces.share', { defaultValue: 'Share' })}>
            <Share2 size={13} />
          </button>
        )}
      </div>
      {sharing && <SharePanel workspace={workspace} />}
      {expanded && (
        <div className="px-2 pb-2 space-y-1">
          {layers.map((l) => (
            <div key={l.key}>
              <div className="flex items-center gap-1 text-xs">
                <button type="button" className={small} onClick={() => toggle(l)}>
                  {isShown(map, l) ? <Eye size={13} /> : <EyeOff size={13} />}
                </button>
                <LayerSwatch layer={l} lang={lang} />
                <span className="flex-1 truncate" title={`${l.count}`}>{l.labels[lang] || l.labels.en || l.key}</span>
                <span className="text-[10px] text-gn-muted">{l.count}</span>
                <AddToGroupMenu item={{ kind: 'company', ref: l.key }} subtle={false} />
                {editable(l) && (
                  <button type="button" className={cn(small, editingKey === l.name && 'bg-gn-blue text-gn-inverse')}
                          title={t('editing.open')} aria-label={t('editing.open')} data-testid={`edit-features-${l.key}`}
                          onClick={() => (editingKey === l.name ? stopEditing() : startEditing(l))}>
                    <PenLine size={13} />
                  </button>
                )}
                {/* anyone who sees the layer may keep their own view of it; the editor says who may change the main one */}
                <button type="button" className={small} title={t('styles.open')} onClick={() => setStyling(l)}>
                  <Palette size={13} />
                </button>
                {l.canUpdate && (
                  <button type="button" className={small} title={t('styles.names')} onClick={() => setEditing(editing === l.key ? null : l.key)}>
                    <Settings2 size={13} />
                  </button>
                )}
                {l.canDelete && (
                  <button type="button" className={small} onClick={() => {
                    if (window.confirm(t('workspaces.delete_confirm', { defaultValue: 'Delete this layer?' }))) remove.mutate(l);
                  }}>
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
              {editing === l.key && (
                <LayerSettings workspace={workspace.id} layerKey={l.key} lang={lang}
                               onSaved={(changed) => { if (map && isShown(map, changed)) show(map, changed); refresh(); }} />
              )}
            </div>
          ))}
          {layers.length === 0 && dataItems.length === 0 && <p className="text-[10px] text-gn-muted italic">{t('workspaces.empty', { defaultValue: 'No layers yet' })}</p>}
          <WorkspaceReports workspace={workspace} />
          {dataItems.length > 0 && (
            <div className="space-y-0.5 pt-1">
              <p className="text-[10px] uppercase tracking-wide text-gn-muted">{t('uploads.data_items')}</p>
              {dataItems.map((d) => (
                <div key={d.key} className="flex items-center gap-1 text-xs" title={t('uploads.data_item_hint')}>
                  <span className="p-1"><Database size={13} className="text-gn-secondary" /></span>
                  <span className="flex-1 truncate">{d.labels[lang] || d.labels.en || d.key}</span>
                  <span className="text-[10px] text-gn-muted">{d.count}</span>
                  {d.canDelete && (
                    <button type="button" className={small} title={t('uploads.data_delete')} onClick={() => {
                      if (window.confirm(t('uploads.data_delete_confirm'))) remove.mutate(d);
                    }}>
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          {running.map((u) => (
            <div key={u.id} className="flex items-center gap-1 text-xs">
              <span className="flex-1 truncate text-gn-secondary">{u.layerName || u.fileName}</span>
              <UploadStatusBadge status={u.status} />
            </div>
          ))}
          {rights?.canUpload && (
            <div className="border-t border-gn-light pt-2">
              <Button size="sm" variant="primary" icon={Upload} onClick={() => setUploading(true)} data-testid="open-upload">
                {t('uploads.open')}
              </Button>
            </div>
          )}
        </div>
      )}
      {styling && (
        <LayerStyleEditor layer={styling} lang={lang} onClose={() => setStyling(null)}
                          onSaved={(changed) => { if (map && !isShown(map, changed)) show(map, changed); refresh(); onChanged(); }} />
      )}
      {uploading && <UploadWizard workspace={workspace.id} workspaceName={workspace.name} rights={rights} onClose={() => setUploading(false)}
                                 onImported={imported} />}
    </div>
  );
};

const LayerSettings: React.FC<{ workspace: string; layerKey: string; lang: string; onSaved: (l: CompanyLayer) => void }> =
  ({ workspace, layerKey, lang, onSaved }) => {
    const { t } = useTranslation();
    const { addToast } = useToast();
    const { data } = useQuery({ queryKey: ['workspaceLayer', workspace, layerKey], queryFn: () => workspaceService.layer(workspace, layerKey) });
    const [label, setLabel] = useState('');
    const [classes, setClasses] = useState<Record<string, string>>({});
    const [labels, setLabels] = useState<Record<string, string>>({});

    useEffect(() => {
      if (!data) return;
      setLabel(data.labels[lang] || data.labels.en || '');
      setClasses(Object.fromEntries((data.fields ?? []).map((f) => [f.key, f.fieldClass])));
      setLabels(Object.fromEntries((data.fields ?? []).map((f) => [f.key, f.labels[lang] || f.labels.en || f.column])));
    }, [data, lang]);

    const save = useMutation({
      mutationFn: () => workspaceService.change(workspace, layerKey, {
        labels: { ...(data?.labels ?? {}), [lang]: label },
        fields: (data?.fields ?? []).map((f) => ({ key: f.key, fieldClass: classes[f.key], labels: { ...f.labels, [lang]: labels[f.key] } })),
      }),
      onSuccess: (l) => { onSaved(l); addToast(t('workspaces.saved', { defaultValue: 'Saved' }), 'success'); },
      onError: (e: any) => addToast(e?.response?.data?.error ?? e.message, 'error'),
    });

    if (!data) return null;
    return (
      <div className="ml-6 my-1 p-2 border border-gn-light rounded space-y-1 text-xs">
        <div className="flex gap-1 items-center">
          <input className={input} value={label} onChange={(e) => setLabel(e.target.value)} />
        </div>
        {(data.fields ?? []).map((f) => (
          <div key={f.key} className="flex gap-1 items-center">
            <input className={input} value={labels[f.key] ?? ''} onChange={(e) => setLabels({ ...labels, [f.key]: e.target.value })} title={f.column} />
            <select className={input} style={{ width: '6.5rem' }} value={classes[f.key] ?? 'none'}
                    onChange={(e) => setClasses({ ...classes, [f.key]: e.target.value })}>
              {CLASSES.map((c) => <option key={c} value={c}>{t(`layerAdmin.class_${c}`, { defaultValue: c })}</option>)}
            </select>
          </div>
        ))}
        <button type="button" className="px-2 py-1 bg-gn-blue text-gn-inverse rounded" disabled={save.isPending} onClick={() => save.mutate()}>
          {t('buttons.save', { defaultValue: 'Save' })}
        </button>
      </div>
    );
  };

const ROLES = ['workspace-viewer', 'workspace-editor', 'workspace-owner'];

const SharePanel: React.FC<{ workspace: Workspace }> = ({ workspace }) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const { data: shares = [] } = useQuery({ queryKey: ['shares', workspace.id], queryFn: () => workspaceService.shares(workspace.id) });
  const { data: colleagues = [] } = useQuery({ queryKey: ['colleagues'], queryFn: () => workspaceService.colleagues() });
  const [who, setWho] = useState('');
  const [role, setRole] = useState(ROLES[0]);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['shares', workspace.id] });
  const share = useMutation({
    mutationFn: () => workspaceService.share(workspace.id, who, role),
    onSuccess: () => { setWho(''); refresh(); },
    onError: (e: Error) => addToast(e.message, 'error'),
  });
  const unshare = useMutation({
    mutationFn: (principal: string) => workspaceService.unshare(workspace.id, principal),
    onSuccess: refresh,
    onError: (e: Error) => addToast(e.message, 'error'),
  });
  return (
    <div className="mx-2 mb-2 p-2 border border-gn-light rounded text-xs space-y-1">
      {shares.map((s) => (
        <div key={s.principalId} className="flex items-center gap-1">
          <span className="flex-1 truncate">{s.displayName}</span>
          <span className="text-[10px] text-gn-muted">{t(`workspaces.role_${s.role}`, { defaultValue: s.role })}</span>
          <button type="button" className={small} onClick={() => unshare.mutate(s.principalId)}><Trash2 size={12} /></button>
        </div>
      ))}
      <div className="flex gap-1">
        <select className={input} value={who} onChange={(e) => setWho(e.target.value)}>
          <option value="">{t('workspaces.colleague', { defaultValue: 'Colleague…' })}</option>
          {colleagues.filter((c) => !shares.some((s) => s.principalId === c.principalId)).map((c) => (
            <option key={c.principalId} value={c.principalId}>{c.displayName}</option>
          ))}
        </select>
        <select className={input} value={role} onChange={(e) => setRole(e.target.value)}>
          {ROLES.map((r) => <option key={r} value={r}>{t(`workspaces.role_${r}`, { defaultValue: r })}</option>)}
        </select>
        <button type="button" className={small} disabled={!who} onClick={() => share.mutate()}><Share2 size={13} /></button>
      </div>
    </div>
  );
};

export default WorkspacePanel;

/** The layer's look in the list: its one colour, or the first colours of its classes. */
const LayerSwatch: React.FC<{ layer: CompanyLayer; lang: string }> = ({ layer, lang }) => {
  const drawn = layer.mine?.style ?? layer.style;
  const items = legendItems(drawn, layer.geometry, lang, '', '');
  if (drawn?.mode === 'heatmap') {
    return <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: 'radial-gradient(#ff0000, #ffff00 45%, #00ff00 70%, #0000ff)' }} />;
  }
  const colours = items.flatMap((i) => (i.kind === 'swatch' ? [i.fill] : [])).slice(0, 3);
  if (colours.length <= 1) return <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: colours[0] ?? '#2563eb' }} />;
  return (
    <span className="inline-flex w-3 h-2.5 rounded-sm overflow-hidden">
      {colours.map((c, i) => <span key={i} className="flex-1" style={{ background: c }} />)}
    </span>
  );
};
